import { Button, Loader } from '@mantine/core'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { useMedplum } from '@medplum/react'
import { useQueryClient } from '@tanstack/react-query'
import type { DocumentReference, Patient } from '@medplum/fhirtypes'
import {
  IconCalendarEvent,
  IconFileText,
  IconInfoCircle,
  IconLock,
  IconPhotoOff,
  IconRefresh,
  IconShield,
  IconShieldCheck,
  IconTrash,
  IconUpload,
  IconUser,
} from '@tabler/icons-react'
import { useCallback, useEffect, useRef, useState, type ChangeEvent, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { ImageZoomModal } from '@/components/ui/ImageZoomModal'
import { patientChartCountsQueryKey } from '@/hooks/patientChartCountsQueryKey'
import { useClient } from '@/hooks/useClient'
import {
  useIdentityVerification,
  type IdentityVerificationData,
} from '@/hooks/useIdentityVerification'
import { usePatient } from '@/hooks/usePatient'
import { IdPhotoCropModal } from '@/pages/Patient/components/IdPhotoCropModal'
import { PersonaAttemptSummary } from '@/pages/Patient/components/PersonaAttemptSummary'
import { PersonaEnrollmentSwitch } from '@/pages/Patient/components/PersonaEnrollmentSwitch'
import { PersonaIdImage } from '@/pages/Patient/components/PersonaIdImage'
import {
  IDENTITY_STATUS_LABELS,
  STATUS_CHIP_CLASSES,
} from '@/pages/Patient/components/identityStatus'
import { formatDateTime } from '@/utils'
import { downloadAttachment } from '@/utils/downloadAttachment'
import { getDocumentAttachmentUrl } from '@/utils/documentReferenceUtils'
import { logError } from '@/utils/errorUtils'
import {
  deletePatientIdentityDocument,
  fetchLatestPatientIdentityDocument,
  uploadPatientIdentityDocument,
  validatePatientIdentityImageFile,
} from '@/utils/patientIdentityDocumentUtils'

const PERSONA_CHECK_NOTIFICATIONS = {
  updated: { color: 'green', message: 'Verification status updated from Persona.' },
  unchanged: { color: 'blue', message: 'No new result from Persona yet.' },
  no_open_attempt: { color: 'blue', message: 'No verification is in progress at Persona.' },
  unavailable: {
    color: 'yellow',
    message: "Persona can't be reached right now. Try again shortly.",
  },
} as const

/** The line under the title: what staff can do next (docs/PERSONA_IDV_PLAN.md §9 target design). */
function nextStepHint(identity: IdentityVerificationData): string {
  const enrolled = identity.personaEnrollment.enrolled
  switch (identity.status) {
    case 'review_required':
      return 'Persona could not complete verification. Continue with manual verification, or the patient can try again from their portal.'
    case 'rejected':
      return 'Identity was rejected. Manual verification is still available.'
    default:
      return enrolled
        ? "Verify the patient's identity using either method below."
        : "Review the patient's ID and verify their identity during the video visit."
  }
}

/** A task's 'YYYY-MM-DD' due date as "Oct 20, 2026" — a calendar day, so no time zone shift. */
function dueDay(date: string): string {
  const [year, month, day] = date.split('-').map(Number)
  return new Date(year, month - 1, day).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function identityStatusLine(identity: IdentityVerificationData): string {
  const parts = [IDENTITY_STATUS_LABELS[identity.status]]
  if (identity.method) {
    parts.push(`Method: ${identity.method === 'persona' ? 'Persona' : 'Manual'}`)
  }
  if (identity.verified && identity.verifiedBy) {
    parts.push(`by ${identity.verifiedBy}`)
  }
  if (identity.verified && identity.verifiedAt) {
    parts.push(formatDateTime(identity.verifiedAt, '/', true))
  }
  return parts.join(' · ')
}

type Props = {
  patient: Patient
  /** Notifies the chart that the Patient resource changed (e.g. photo set). */
  onPatientUpdated?: () => void
}

export function PatientIdentityVerificationCard({ patient, onPatientUpdated }: Props) {
  const medplum = useMedplum()
  const client = useClient()
  const queryClient = useQueryClient()
  const { data: patientData, refresh: refreshPatient } = usePatient(patient.id)
  const {
    data: identity,
    loading: loadingIdentity,
    refresh: refreshIdentity,
  } = useIdentityVerification(patient.id)
  const [verifying, setVerifying] = useState(false)
  const identityVerified = patientData?.identityVerified ?? false
  const inputRef = useRef<HTMLInputElement>(null)
  const [identityDocument, setIdentityDocument] = useState<DocumentReference | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewError, setPreviewError] = useState(false)
  const [loadingDocument, setLoadingDocument] = useState(Boolean(patient.id))
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [zoomOpen, setZoomOpen] = useState(false)
  // PD-1282 — offer the portrait on the ID as the patient photo.
  const [idPhotoFile, setIdPhotoFile] = useState<File | null>(null)
  const [manualOpen, setManualOpen] = useState(false)
  const [checking, setChecking] = useState(false)

  useEffect(() => {
    let active = true

    const loadDocument = async () => {
      if (!patient.id) {
        setIdentityDocument(null)
        setPreviewError(false)
        setLoadingPreview(false)
        setLoadingDocument(false)
        return
      }

      setLoadingDocument(true)
      try {
        const document = await fetchLatestPatientIdentityDocument(medplum, patient.id)
        if (active) {
          setLoadingPreview(Boolean(document && getDocumentAttachmentUrl(document)))
          setPreviewError(false)
          setIdentityDocument(document)
        }
      } catch (error) {
        logError('patient', 'Fetch patient identity document', error)
        if (active) {
          setPreviewError(false)
          setLoadingPreview(false)
          setIdentityDocument(null)
        }
      } finally {
        if (active) {
          setLoadingDocument(false)
        }
      }
    }

    void loadDocument()

    return () => {
      active = false
    }
  }, [medplum, patient.id])

  useEffect(() => {
    let active = true
    let objectUrl: string | undefined

    const loadPreview = async () => {
      setPreviewUrl(null)
      setPreviewError(false)
      const attachmentUrl = identityDocument ? getDocumentAttachmentUrl(identityDocument) : null
      if (!attachmentUrl) {
        setPreviewError(Boolean(identityDocument))
        setLoadingPreview(false)
        return
      }

      setLoadingPreview(true)
      try {
        const blob = await downloadAttachment(medplum, attachmentUrl)
        objectUrl = URL.createObjectURL(blob)
        if (!active) {
          URL.revokeObjectURL(objectUrl)
          return
        }
        setPreviewUrl(objectUrl)
      } catch (error) {
        logError('patient', 'Load patient identity document preview', error)
        if (active) {
          setPreviewUrl(null)
          setPreviewError(true)
        }
      } finally {
        if (active) {
          setLoadingPreview(false)
        }
      }
    }

    void loadPreview()

    return () => {
      active = false
      if (objectUrl) {
        const urlToRevoke = objectUrl
        setPreviewUrl((currentUrl) => {
          if (currentUrl === urlToRevoke) {
            URL.revokeObjectURL(urlToRevoke)
            return null
          }
          return currentUrl
        })
      }
    }
  }, [identityDocument, medplum])

  const handleUpload = useCallback(
    async (file: File) => {
      const validationError = validatePatientIdentityImageFile(file)
      if (validationError) {
        showNotification({ color: 'red', message: validationError })
        return
      }

      setUploading(true)
      try {
        const { document, supersedeFailed } = await uploadPatientIdentityDocument(
          medplum,
          file,
          patient,
        )
        setIdentityDocument(document)
        try {
          medplum.invalidateSearches('DocumentReference')
          // An ID newer than the verification puts the patient back in review: re-read the status
          // so the card offers the review rather than still showing them verified.
          await Promise.all([refreshPatient(), refreshIdentity()])
          if (patient.id) {
            await queryClient.invalidateQueries({
              queryKey: patientChartCountsQueryKey(patient.id),
            })
          }
        } catch (error) {
          logError('patient', 'Refresh patient identity document caches', error)
        }
        showNotification(
          supersedeFailed
            ? {
                color: 'yellow',
                title: 'Patient ID uploaded',
                message:
                  'Some previous ID records could not be marked superseded. The newest ID is available.',
              }
            : { color: 'green', message: 'Patient ID uploaded successfully.' },
        )

        // Opens the photo modal immediately; it shows its own detecting state
        // while the portrait lookup runs.
        setIdPhotoFile(file)
      } catch (error) {
        logError('patient', 'Upload patient identity document', error)
        showNotification({
          color: 'red',
          message: error instanceof Error ? error.message : 'Unable to upload patient ID.',
        })
      } finally {
        setUploading(false)
      }
    },
    [medplum, patient, queryClient, refreshPatient, refreshIdentity],
  )

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.currentTarget.files?.[0]
    event.currentTarget.value = ''
    if (!file) {
      return
    }

    void handleUpload(file)
  }

  const handleDelete = useCallback(() => {
    if (!identityDocument) {
      return
    }

    const performDelete = async () => {
      setDeleting(true)
      let revoked = false
      try {
        if (identityVerified && patient.id) {
          await client.markIdentityVerified({ id: patient.id, verified: false })
          await Promise.all([refreshPatient(), refreshIdentity()])
          revoked = true
        }

        await deletePatientIdentityDocument(medplum, identityDocument)
        setIdentityDocument(null)
        setPreviewUrl(null)
        setPreviewError(false)

        try {
          medplum.invalidateSearches('DocumentReference')
          if (patient.id) {
            await queryClient.invalidateQueries({
              queryKey: patientChartCountsQueryKey(patient.id),
            })
          }
        } catch (error) {
          logError('patient', 'Refresh patient identity document caches', error)
        }

        showNotification({ color: 'green', message: 'Patient ID deleted.' })
      } catch (error) {
        logError('patient', 'Delete patient identity document', error)
        showNotification({
          color: 'red',
          message: revoked
            ? 'Identity verification was revoked, but deleting the ID document failed. Re-verify manually if appropriate.'
            : error instanceof Error
              ? error.message
              : 'Unable to delete patient ID.',
        })
      } finally {
        setDeleting(false)
      }
    }

    modals.openConfirmModal({
      title: "Delete driver's license / state ID?",
      children: identityVerified
        ? "This removes the uploaded ID and revokes the patient's identity verification, restricting portal access until it is verified again."
        : 'This removes the uploaded ID. It can be re-uploaded at any time.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: () => {
        void performDelete()
      },
    })
  }, [
    client,
    identityDocument,
    identityVerified,
    medplum,
    patient.id,
    queryClient,
    refreshPatient,
    refreshIdentity,
  ])

  const setIdentityVerified = (verified: boolean) => {
    if (!patient.id) {
      return
    }

    const performSetVerified = async () => {
      setVerifying(true)
      try {
        await client.markIdentityVerified({ id: patient.id as string, verified })
        await Promise.all([refreshPatient(), refreshIdentity()])
        await queryClient.invalidateQueries({
          queryKey: patientChartCountsQueryKey(patient.id as string),
        })
        setManualOpen(false)
        showNotification({
          color: 'green',
          message: verified
            ? 'Identity verified. Full portal access unlocked.'
            : 'Identity verification removed.',
        })
      } catch (error) {
        logError('patient', 'Set patient identity verification', error)
        showNotification({
          color: 'red',
          message:
            error instanceof Error ? error.message : 'Unable to update identity verification.',
        })
      } finally {
        setVerifying(false)
      }
    }

    modals.openConfirmModal({
      title: verified ? 'Mark identity verified?' : 'Remove identity verification?',
      children: verified
        ? "This unlocks full patient portal access — medical records, lab results, messages, and care plans. Confirm you have verified this patient's government ID."
        : "This re-restricts the patient's portal to scheduling, intake, and insurance until their identity is verified again.",
      labels: { confirm: verified ? 'Mark verified' : 'Remove verification', cancel: 'Cancel' },
      confirmProps: { color: verified ? 'green' : 'red' },
      onConfirm: () => {
        void performSetVerified()
      },
    })
  }

  // Results reach us by webhook or the 5-minute sweep; this asks Persona now (the GET then re-reads).
  const checkWithPersona = async () => {
    if (!patient.id) {
      return
    }
    setChecking(true)
    try {
      const { outcome } = await client.checkIdentityVerification({ id: patient.id })
      await Promise.all([refreshPatient(), refreshIdentity()])
      if (outcome === 'updated') {
        await queryClient.invalidateQueries({
          queryKey: patientChartCountsQueryKey(patient.id),
        })
      }
      showNotification(PERSONA_CHECK_NOTIFICATIONS[outcome])
    } catch (error) {
      logError('patient', 'Check identity verification with Persona', error)
      showNotification({
        color: 'red',
        message: error instanceof Error ? error.message : 'Unable to check with Persona.',
      })
    } finally {
      setChecking(false)
    }
  }

  const hasDocument = Boolean(identityDocument)
  const statusLabel = loadingDocument ? 'Loading' : hasDocument ? 'Uploaded' : 'Not uploaded'
  const statusClassName = loadingDocument
    ? 'border-blue-100 bg-blue-50 text-blue-700'
    : hasDocument
      ? 'border-green-200 bg-green-50 text-green-700'
      : 'border-slate-200 bg-slate-50 text-slate-600'

  const unverified = identity ? !identity.verified : !identityVerified
  const inReview = identity?.status === 'review_required'
  // The two choices side by side; a rejected patient is never offered Persona (§4 rule 3).
  const showOptions =
    !!identity && (identity.status === 'not_started' || identity.status === 'pending')
  const enrollmentSwitch =
    patient.id && identity ? (
      <PersonaEnrollmentSwitch
        patientId={patient.id}
        enrollment={identity.personaEnrollment}
        onChanged={refreshIdentity}
      />
    ) : null
  const startManual = (variant: 'filled' | 'outline') => (
    <Button
      size="sm"
      variant={variant}
      disabled={manualOpen || !patientData}
      onClick={() => setManualOpen(true)}
    >
      Start manual verification
    </Button>
  )

  return (
    <section className="mt-6 rounded-lg border border-[#D0E1F2] bg-white p-5 shadow-sm">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-3">
            <span className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
              <IconShield size={22} aria-hidden />
            </span>
            <h3 className="text-xl font-semibold text-slate-900">Identity verification</h3>
            {identity ? (
              <span
                className={`rounded-full border px-2.5 py-0.5 text-xs font-medium ${STATUS_CHIP_CLASSES[identity.status]}`}
              >
                {IDENTITY_STATUS_LABELS[identity.status]}
              </span>
            ) : null}
          </div>
          {identity ? (
            identity.verified ? (
              <>
                <p className="mt-1 text-sm text-slate-700" data-testid="identity-status-line">
                  {identityStatusLine(identity)}
                </p>
                <p className="mt-1 text-xs text-green-700">Health records access enabled</p>
              </>
            ) : (
              <p className="mt-1 text-sm text-[#64748B]">{nextStepHint(identity)}</p>
            )
          ) : loadingIdentity ? (
            // Status, choices and the Persona attempt all arrive with this — say so rather than
            // let them pop in.
            <p role="status" className="mt-1 flex items-center gap-2 text-sm text-[#64748B]">
              <Loader size="xs" />
              Loading verification status…
            </p>
          ) : null}
        </div>
        {inReview ? (
          <div className="flex shrink-0 flex-col items-stretch gap-2">
            {startManual('filled')}
            {enrollmentSwitch}
          </div>
        ) : null}
      </div>

      {showOptions && identity ? (
        <div className="mb-3 grid gap-3 md:grid-cols-2">
          <OptionTile
            icon={IconUser}
            title="Automated verification"
            subtitle="Powered by Persona"
            text={
              identity.personaEnrollment.enrolled
                ? 'The patient verifies their identity in the patient portal using an ID and a selfie.'
                : 'Not offered to this patient. Enable it to offer ID and selfie verification in their portal.'
            }
          >
            {enrollmentSwitch}
          </OptionTile>
          <OptionTile
            icon={IconFileText}
            title="Manual verification"
            text="Review the patient's ID and confirm their identity during the video visit."
          >
            {startManual('filled')}
          </OptionTile>
        </div>
      ) : null}

      {identity?.status === 'rejected' ? (
        <div className="mb-3">{startManual('outline')}</div>
      ) : null}

      {identity?.latestAttempt ? (
        <div className="mb-3 space-y-2">
          <PersonaAttemptSummary attempt={identity.latestAttempt} />
          {/* Still open at Persona: nothing decided and not closed by us. */}
          {!identity.latestAttempt.decision && !identity.latestAttempt.closedReason ? (
            <Button
              size="xs"
              variant="light"
              leftSection={<IconRefresh size={14} />}
              loading={checking}
              onClick={() => void checkWithPersona()}
            >
              Check with Persona
            </Button>
          ) : null}
        </div>
      ) : null}

      {identity && unverified && identity.followUpTask ? (
        <div
          className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-md border border-[#E2E8F0] px-3 py-2 text-sm text-slate-700"
          data-testid="identity-follow-up-task"
        >
          <span className="flex items-center gap-2">
            <IconCalendarEvent size={16} aria-hidden />
            {[
              'Manual follow-up',
              identity.followUpTask.assignee
                ? `Assigned to ${identity.followUpTask.assignee}`
                : 'Not assigned yet',
              identity.followUpTask.dueDate
                ? `Due ${dueDay(identity.followUpTask.dueDate)}`
                : undefined,
            ]
              .filter(Boolean)
              .join(' · ')}
          </span>
          <Link
            className="text-primary text-sm font-medium hover:underline"
            to={`/appointments?tab=care-queue&board=${identity.followUpTask.boardId}&item=${identity.followUpTask.itemId}`}
          >
            View task
          </Link>
        </div>
      ) : null}

      {manualOpen ? (
        <div
          className="mb-4 rounded-lg border border-[#D0E1F2] p-4"
          role="region"
          aria-label="Manual verification"
        >
          <p className="text-base font-semibold text-slate-900">Manual verification</p>
          <p className="mt-1 text-sm text-[#64748B]">
            Review the uploaded ID and compare it with the patient during the video visit.
          </p>
          <Button
            mt="sm"
            size="sm"
            variant="outline"
            disabled={!previewUrl}
            onClick={() => setZoomOpen(true)}
          >
            View uploaded ID
          </Button>
          {!hasDocument && !loadingDocument ? (
            <p className="mt-1 text-xs text-[#64748B]">No ID uploaded yet. Upload one below.</p>
          ) : null}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
            <p className="flex items-center gap-1 text-xs text-[#64748B]">
              <IconInfoCircle size={14} aria-hidden />
              Verification method, reviewer and time are recorded automatically.
            </p>
            <div className="flex gap-2">
              <Button size="sm" variant="default" onClick={() => setManualOpen(false)}>
                Cancel
              </Button>
              <Button
                size="sm"
                leftSection={<IconShieldCheck size={16} />}
                loading={verifying}
                disabled={!patientData}
                onClick={() => setIdentityVerified(true)}
              >
                Mark identity verified
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* The patient reached Persona's ID step: the ID it checked, shown live from Persona. */}
      {patient.id && identity?.latestAttempt?.governmentIdResult ? (
        <PersonaIdImage
          patientId={patient.id}
          attemptId={identity.latestAttempt.id}
          verified={identity.status === 'verified_auto' && identity.method === 'persona'}
        />
      ) : null}

      <div className="flex flex-col gap-4 rounded-lg border border-[#E2E8F0] p-4 md:flex-row md:items-center">
        {/* The ID's thumbnail once there is one; until then the same round icon as the tiles. */}
        {hasDocument || loadingDocument ? (
          <div
            className={`flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded-md border border-[#D8E1EC] bg-[#F8FAFC] text-[#8A98AD] ${previewUrl ? 'hover:border-primary cursor-pointer transition' : ''}`}
            role={previewUrl ? 'button' : undefined}
            tabIndex={previewUrl ? 0 : undefined}
            aria-label={previewUrl ? 'Enlarge driver license or state ID' : undefined}
            onClick={() => {
              if (previewUrl) {
                setZoomOpen(true)
              }
            }}
            onKeyDown={(event) => {
              if (previewUrl && (event.key === 'Enter' || event.key === ' ')) {
                event.preventDefault()
                setZoomOpen(true)
              }
            }}
          >
            {loadingDocument || loadingPreview ? (
              <Loader size="sm" />
            ) : previewUrl ? (
              <img
                src={previewUrl}
                alt="Driver license or state ID preview"
                className="h-full w-full object-contain"
              />
            ) : previewError ? (
              <IconPhotoOff size={24} aria-label="Preview unavailable" />
            ) : null}
          </div>
        ) : (
          <span className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
            <IconFileText size={20} aria-hidden />
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <p className="text-base font-semibold text-slate-900">
              Driver&apos;s license / State ID
            </p>
            <span
              className={`rounded-full border px-3 py-1 text-xs font-medium ${statusClassName}`}
            >
              {statusLabel}
            </span>
          </div>
          <p className="mt-1 text-sm text-[#64748B]">
            Upload the front of the patient&apos;s ID for check-in review.
          </p>
          <p className="text-xs text-[#94A3B8]">Uploading an ID does not complete verification.</p>
          <input
            ref={inputRef}
            type="file"
            accept="image/png,image/jpeg,.png,.jpg,.jpeg"
            className="hidden"
            onChange={onFileChange}
          />
        </div>

        <div className="flex flex-wrap items-center gap-2 md:justify-end">
          <Button
            size="sm"
            variant="outline"
            leftSection={<IconUpload size={16} />}
            loading={uploading}
            disabled={loadingDocument}
            onClick={() => inputRef.current?.click()}
          >
            {hasDocument ? 'Replace ID' : 'Upload ID'}
          </Button>
          {hasDocument ? (
            <Button
              size="sm"
              variant="subtle"
              color="red"
              leftSection={<IconTrash size={16} />}
              loading={deleting}
              disabled={loadingDocument}
              onClick={handleDelete}
            >
              Delete ID
            </Button>
          ) : null}
          {identityVerified ? (
            <Button
              size="sm"
              variant="subtle"
              color="gray"
              loading={verifying}
              disabled={!patientData}
              onClick={() => setIdentityVerified(false)}
            >
              Undo verification
            </Button>
          ) : null}
          {/* A verified patient has nothing to verify, unless they are still enrolled. */}
          {identity?.verified && identity.personaEnrollment.enrolled ? enrollmentSwitch : null}
        </div>
      </div>

      {identity && unverified ? (
        <div className="mt-3 space-y-2">
          <p className="flex items-center gap-2 rounded-md border border-blue-100 bg-blue-50 px-3 py-2 text-sm text-blue-800">
            <IconInfoCircle size={16} aria-hidden />
            Patients can schedule and join visits while verification is pending.
          </p>
          <p className="flex items-center gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <IconLock size={16} aria-hidden />
            Health records remain restricted until identity is verified.
          </p>
        </div>
      ) : null}

      <ImageZoomModal
        opened={zoomOpen}
        imageUrl={previewUrl}
        title="Driver's license / State ID"
        imageAlt="Driver license or state ID"
        onClose={() => setZoomOpen(false)}
      />

      <IdPhotoCropModal
        patientId={patient.id}
        file={idPhotoFile}
        onClose={() => setIdPhotoFile(null)}
        onSaved={() => {
          void refreshPatient()
          onPatientUpdated?.()
        }}
      />
    </section>
  )
}

function OptionTile({
  icon: TileIcon,
  title,
  subtitle,
  text,
  children,
}: {
  icon: typeof IconUser
  title: string
  subtitle?: string
  text: string
  children: ReactNode
}) {
  return (
    <div className="flex gap-3 rounded-lg border border-[#E2E8F0] p-4">
      <span className="bg-primary/10 text-primary flex h-10 w-10 shrink-0 items-center justify-center rounded-full">
        <TileIcon size={20} aria-hidden />
      </span>
      <div className="min-w-0">
        <p className="font-semibold text-slate-900">{title}</p>
        {subtitle ? <p className="text-sm text-[#94A3B8]">{subtitle}</p> : null}
        <p className="mt-1 text-sm text-[#64748B]">{text}</p>
        <div className="mt-3">{children}</div>
      </div>
    </div>
  )
}
